import { useState, useMemo, useCallback } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { stageOrder } from '@/components/Deals/deals-constants'
import { RocketKanbanColumn } from '@/components/RocketSearch/RocketKanbanColumn'
import { useRocketKanbanScroll } from '@/components/RocketSearch/useRocketKanbanScroll'
import type { Deal, DealStage } from '@/types'

export interface RocketKanbanProps {
  deals: Deal[]
  loading?: boolean
  onMoveStage?: (dealId: string, newStage: DealStage) => void
  onDelete?: (dealId: string) => void
  closerFilter?: string
  onDealClick?: (deal: Deal) => void
}

export function RocketKanban({
  deals,
  loading = false,
  onMoveStage,
  onDelete,
  closerFilter,
  onDealClick,
}: RocketKanbanProps) {
  const [draggedDealId, setDraggedDealId] = useState<string | null>(null)
  const { containerRef, showLeft, showRight, checkScroll, scroll } = useRocketKanbanScroll([deals])

  const filteredDeals = useMemo(() => {
    if (!closerFilter) return deals
    return deals.filter((d) => d.closerId === closerFilter || d.closerNome === closerFilter)
  }, [deals, closerFilter])

  const stageMap = useMemo(() => {
    const map = new Map<DealStage, Deal[]>()
    for (const stage of stageOrder) map.set(stage, [])
    for (const deal of filteredDeals) {
      const list = map.get(deal.etapa)
      if (list) list.push(deal)
      else map.set(deal.etapa, [deal])
    }
    return map
  }, [filteredDeals])

  const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, dealId: string) => {
    e.dataTransfer.setData('text/plain', dealId)
    e.dataTransfer.effectAllowed = 'move'
    setDraggedDealId(dealId)
  }, [])

  const handleDragEnd = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDraggedDealId(null)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent, stage: DealStage) => {
      e.preventDefault()
      const dealId = e.dataTransfer.getData('text/plain')
      if (dealId && onMoveStage) {
        onMoveStage(dealId, stage)
      }
      setDraggedDealId(null)
    },
    [onMoveStage],
  )

  if (loading) {
    return (
      <div className='flex items-center justify-center py-16'>
        <div className='h-6 w-6 animate-spin rounded-full border-2 border-[var(--brand-primary)]/30 border-t-[var(--brand-primary)]' />
      </div>
    )
  }

  return (
    <div className='relative'>
      {showLeft && (
        <button type='button' onClick={() => scroll('left')} className='kanban-nav-btn left-btn'>
          <ChevronLeft size={16} />
        </button>
      )}
      {showRight && (
        <button type='button' onClick={() => scroll('right')} className='kanban-nav-btn right-btn'>
          <ChevronRight size={16} />
        </button>
      )}

      <div
        ref={containerRef}
        onScroll={checkScroll}
        className='scrollbar-thin flex gap-3 overflow-x-auto pb-4'
      >
        {stageOrder.map((stage) => (
          <RocketKanbanColumn
            key={stage}
            stage={stage}
            stageDeals={stageMap.get(stage) ?? []}
            draggedDealId={draggedDealId}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDealClick={onDealClick}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  )
}
